import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useAnnouncements, type Announcement } from './useAnnouncements';

// ─── Styles per priority ─────────────────────────────────────────────────────

const priorityStyles: Record<Announcement['priority'], string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200',
  warning:
    'bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200',
  critical:
    'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200',
};

const priorityIconColor: Record<Announcement['priority'], string> = {
  info: 'text-blue-500 dark:text-blue-400',
  warning: 'text-yellow-500 dark:text-yellow-400',
  critical: 'text-red-500 dark:text-red-400',
};

function PriorityIcon({ priority }: { priority: Announcement['priority'] }) {
  if (priority === 'critical') {
    return (
      <svg
        className="h-5 w-5 flex-shrink-0"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (priority === 'warning') {
    return (
      <svg
        className="h-5 w-5 flex-shrink-0"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  return (
    <svg
      className="h-5 w-5 flex-shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ─── Single banner item ───────────────────────────────────────────────────────

function AnnouncementItem({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 border-b px-4 py-3 sm:px-6',
        priorityStyles[announcement.priority],
      )}
    >
      <span className={cn('mt-0.5', priorityIconColor[announcement.priority])}>
        <PriorityIcon priority={announcement.priority} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{announcement.title}</p>
        {announcement.body && (
          <p className="mt-0.5 text-sm opacity-90 whitespace-pre-line">{announcement.body}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(announcement.id)}
        className="flex-shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        aria-label={`Dismiss announcement: ${announcement.title}`}
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Displays active platform announcements as dismissible banners at the top of the layout.
 * Dismissed announcements are remembered in sessionStorage (clears on tab close).
 * Critical announcements are shown first.
 */
export function AnnouncementBanner() {
  const { data: announcements } = useAnnouncements();

  // Track dismissed IDs in sessionStorage so they don't reappear on navigation
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('dismissed_announcements');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const handleDismiss = (id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      try {
        sessionStorage.setItem('dismissed_announcements', JSON.stringify([...next]));
      } catch {
        // sessionStorage unavailable — dismiss in-memory only
      }
      return next;
    });
  };

  if (!announcements || announcements.length === 0) return null;

  // Sort: critical first, then warning, then info. Filter dismissed.
  const priorityOrder = { critical: 0, warning: 1, info: 2 };
  const visible = announcements
    .filter((a) => !dismissed.has(a.id))
    .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  if (visible.length === 0) return null;

  return (
    <div aria-label="Platform announcements">
      {visible.map((announcement) => (
        <AnnouncementItem
          key={announcement.id}
          announcement={announcement}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
}
